/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp.  2017, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/View',
	'./ActiveFilterIndicator',
	'doT',
	'text!./templates/filterCollapsibleView.html',
	'../../widgets/livewidget/nls/StringResources'
], function(View, ActiveFilterIndicator, doT, filterCollapsibleView, stringResources) {

	var FilterContainerCollapseView = View.extend([], {
		id: 'filter-dock-collapse-handle-container',

		events: {
			'primaryaction': 'onClick'
		},

		/**
		 * Constructor
		 *
		 * @param {object} options
		 * @param {object} options.viewModel - view model
		 *
		 */
		init: function(options) {
			FilterContainerCollapseView.inherited('init', this, arguments);
			this.dashboard = options.dashboardApi;
			this._filterContainerView = this.dashboard.getFeature('.CanvasExtensions').getExtension('filterDock');
			this._isExpand = false;
			/** Currently extraOptions support the followig rules
			 * extraClassName: 'something'
			 * handleIcon: 'somethingIcon', default to undefined. Using inline svg
			 * showActiveIndicator: true/false default to true
			 */
			this.extraOptions = options.extraOptions;
			this.targetId = options.targetId || 'filterDockContainer';
			this.dashboard.on('tab:tabChanged', this.onScopeChanged, this);
			this.dashboard.on('pagecontext:filterContextUpdated', this.onFilterContextUpdated, this);
			this.dashboard.on('filterDock:disable', this.disable, this);
			this.dashboard.on('filterDock:enable', this.enable, this);
			this.dashboard.on('filterDock:collapse', this.collapse, this);
			this.dashboard.on('filterDock:expand', this.expand, this);
			// TODO: dashboard.getFeature('internal') is a deprecated internal dashboard api.
			//		We should use API events when they support undo.
			this.canvas = this.dashboard.getCanvas();
			if(this.canvas.getPropertyNameList().indexOf('fillColor') > -1){
				let boardModel = this.dashboard.getFeature('internal').getBoardModel();
				this.removeModelEventHandler = boardModel.on('change:layout', this._onChangeLayout, this);
				this._colorsFeature = this.dashboard.getFeature('Colors');
				let boardFillColor = this.canvas.getPropertyValue('fillColor');
				this._backgroundColorClass = 'fill-' + this._colorsFeature.getColorClassName(boardFillColor);
				this._applyBackgroundColor();
			} else {
				this._backgroundColorClass = '';
			}

			this.tooltipExpand = stringResources.get('expand');
			this.tooltipCollapse = stringResources.get('collapse');

			this.contentTemplateFn = function() {
				return doT.template(filterCollapsibleView)({ extraOptions: this.extraOptions });
			};

		},

		_onChangeLayout(payload) {
			if(payload.eventName === 'change:fillColor'){
				let boardFillColor = this.canvas.getPropertyValue('fillColor');
				if(this.isExpanded()){
					this._backgroundColorClass = 'fill-' + this._colorsFeature.getColorClassName(boardFillColor);
				}else{
					this._removeBackgroundColor();
					this._backgroundColorClass = 'fill-' + this._colorsFeature.getColorClassName(boardFillColor);
					this._applyBackgroundColor();
				}
			}
		},

		_removeBackgroundColor() {
			this.$el.removeClass(this._backgroundColorClass);
		},

		_applyBackgroundColor() {
			this.$el.addClass(this._backgroundColorClass);
		},

		remove: function() {
			if (this.afi) {
				this.afi.remove();
				this.afi = null;
			}

			this.dashboard.off('tab:tabChanged', this.onScopeChanged, this);
			this.dashboard.off('pagecontext:filterContextUpdated', this.onFilterContextUpdated, this);
			this.dashboard.off('filterDock:disable', this.disable, this);
			this.dashboard.off('filterDock:enable', this.enable, this);
			this.dashboard.off('filterDock:collapse', this.collapse, this);
			this.dashboard.off('filterDock:expand', this.expand, this);
			this.$('#filter-dock-collapse-handle-icon').off('click');
			this.removeModelEventHandler && this.removeModelEventHandler.remove();

			FilterContainerCollapseView.inherited('remove', this, arguments);
		},

		_getCurrentScopeId: function () {
			var boardModel = this.dashboard.getFeature('internal').getBoardModel();
			let currentScopeId;
			if (boardModel.layout && boardModel.layout.items.length > 0 && boardModel.layout.items[0] && boardModel.layout.items[0].id) {
				currentScopeId = boardModel.layout.items[0].id;
			}
			return currentScopeId;
		},

		_getPageContextAPI: function () {
			return Promise.resolve(this.dashboard.getFeature('PageContextService').getPageContext());
		},

		/**
		 * On click handler
		 */
		onClick: function() {
			if (this.isExpanded()) {
				this.dashboard.triggerDashboardEvent('filterDock:collapse');
			} else {
				this.dashboard.triggerDashboardEvent('filterDock:expand');
			}
		},

		disable: function() {
			this.$el.addClass('filter-dock-disabled');
			this._filterContainerView && this._filterContainerView.disable();
		},

		enable: function() {
			this.$el.removeClass('filter-dock-disabled');
			this._filterContainerView && this._filterContainerView.enable();
		},

		isExpanded: function() {
			return this._isExpand;
		},

		_toggleExpand: function(collapse) {
			this.$el.toggleClass('filter-dock-collapse-handle-container-expanded', collapse);
			this.$el.toggleClass('filter-dock-collapse-handle-container-collapsed', !collapse);
			var tooltip = collapse ? this.tooltipExpand : this.tooltipCollapse;
			this.$el.find('#filter-dock-collapse-handle-icon').attr('title', tooltip);
			this.$el.find('.filterDockCollapseHandle').attr('area-label', tooltip);
			if (this.afi) {
				this.afi.show(collapse);
			}
		},

		expand: function() {
			this._toggleExpand(false);
			this._filterContainerView.expand();
			this._isExpand = true;
			this._removeBackgroundColor();
			// @todo: remove the filterDock:expanded event when EPIC CADBLW-6693 is done
			this.dashboard.triggerDashboardEvent('filterDock:expanded');
		},

		collapse: function() {
			this._toggleExpand(true);
			this._filterContainerView.collapse();
			this._isExpand = false;
			this._applyBackgroundColor();
		},

		changeToConsumeMode: function() {
			this._filterContainerView.changeToConsumeMode();
			return this._getPageContextAPI().then((pageContextAPI) => {
				const filterEntries = pageContextAPI.getPageContextItems({ origin: 'filter', _strictMatch: true });
				const filterCount = filterEntries.length;
				if (filterCount === 0) {
					this.collapse();
				}
			});
		},

		changeToEditMode: function() {
			if(!this.isExpanded()){
				this.expand();
			}
			this._filterContainerView.changeToAuthorMode();
		},

		/**
		 * Method to show/hide the collapsible view
		 *
		 * @param {boolean} state - TRUE to show and FALSE to hide
		 */
		show: function(state) {
			state = !!state;
			this.$el.toggle(state);
		},

		// TODO:
		// 1. move from lifeCycleManager to DashboardState
		// 2. we assume the FilterContainerView class is loaded before this class which is true but not necessary.
		//     It would be better if we remove the dependency.
		_setupLifeCycleHandlers() {
			this.dashboard.getDashboardSvc('.LifeCycleManager')
				.then(LifeCycleManager => {
					LifeCycleManager.registerLifeCycleHandler('post:mode.edit', this.changeToEditMode.bind(this));
					LifeCycleManager.registerLifeCycleHandler('post:mode.consume', this.changeToConsumeMode.bind(this));
				});
		},

		render: function() {
			return Promise.all([
				this._getPageContextAPI()
			]).then(function (Result) {
				const currentScopeId = this._getCurrentScopeId();
				const pageContextAPI = Result[0];
				const filterEntries = pageContextAPI.getPageContextItems({ origin: 'filter', _strictMatch: true });
				const filterCount = filterEntries.length;
				this.$el.empty();
				this.$el.html(this.dotTemplate({}));
				this.$el.append(this.contentTemplateFn());
				if (this.dashboard.getMode() !== 'authoring' && filterCount === 0) {
					this.collapse();
				} else {
					this.expand();
				}

				// by default afi is rendered unless showActiveIndicator is falsy in extraOptions
				if (!this.extraOptions || this.extraOptions.showActiveIndicator) {
					this.createAndShowAPI(currentScopeId, pageContextAPI);
				}
				this._setupLifeCycleHandlers();
			}.bind(this));
		},

		createAndShowAPI: function(currentScopeId, pageContextAPI) {
			this.afi = new ActiveFilterIndicator({
				scopes: {
					tabScope: currentScopeId,
					globalScope: 'global'
				},
				pageContextAPI: pageContextAPI,
				location: 'Collapse'
			});
			this.$el.find('.filter-dock-collapse-count').append(this.afi.getNode());
			this.afi.show(false);
		},

		/**
		 * Method to react to the tab scope changing, and updating the scope accordingly
		 * The tab scope needs to be updated to determine the correct number of active filters
		 *
		 * @param {Object} source - object containing the tab scope as the modelId
		 */
		onScopeChanged: function(source) {
			this.scope = source.modelId;
			if (this.afi) {
				this.afi.setScope('tabScope', this.scope);
				this.afi.show(!this.isExpanded());
			}
		},

		/**
		 * Method to react to the filter context updating
		 * Important for collapsed docks and filter widgets on the canvas becoming inactive
		 */
		onFilterContextUpdated: function(payload) {
			payload = payload || {};
			if (payload.ignorePageContextChanged && payload.ignorePageContextChanged()) {
				return;
			}
			if (this.afi) {
				this.afi.show();
			}
		}
	});

	return FilterContainerCollapseView;
});